import { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  Direction,
  Flex,
  Link,
  OverlayStyle,
  SlideoutDrawerV2,
  Tab,
  Tabs,
  Text,
} from "@jsq/ds/core";
import { Colors } from "@jsq/ds/styles";
import * as styles from "../styles_vanilla.css";
import { QueryReferenceTab } from "./QueryReferenceTab";
import type { DrawerMode, DrawerTab } from "../types";

const DRAWER_Z_INDEX = 1050;

export type TemplateReferenceDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  mode?: DrawerMode;
  initialTab?: DrawerTab;
  queryId: number | null;
  arenaId: number | null;
  dataPackageId: number | null;
  dataStructureBasePath: string;
};

export const TemplateReferenceDrawer = ({
  isOpen,
  onClose,
  mode = "full",
  initialTab = "instructions",
  queryId,
  arenaId,
  dataPackageId,
  dataStructureBasePath,
}: TemplateReferenceDrawerProps) => {
  const [activeTab, setActiveTab] = useState<DrawerTab>(initialTab);

  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
    }
  }, [isOpen, initialTab]);

  const handleTabClick = useCallback((tab: DrawerTab) => {
    setActiveTab(tab);
  }, []);

  const title = mode === "query-only" ? "Query reference" : "Create and preview your template";
  const isWideContent = mode === "query-only" || activeTab === "query-reference";

  const closeButton = (
    <Button
      variant="custom"
      textColor={Colors.Neutral700}
      backgroundColor={Colors.Neutral0}
      icon="arrow-right-to-line"
      iconPosition="right"
      onClick={onClose}
    >
      Close
    </Button>
  );

  return (
    <SlideoutDrawerV2
      isOpen={isOpen}
      onClose={onClose}
      direction={Direction.Right}
      overlayStyle={OverlayStyle.Transparent}
      zIndex={DRAWER_Z_INDEX}
    >
      <Flex
        flexDirection="column"
        className={isWideContent ? styles.drawerShellWide : styles.drawerShell}
      >
        <SlideoutDrawerV2.ComplexHeader
          title={title}
          description={title}
          className={styles.drawerHeader}
          showBorder={false}
          closeButton={closeButton}
        />

        {mode === "full" && (
          <Tabs className={styles.drawerTabs}>
            <Tab
              text="Instructions"
              active={activeTab === "instructions"}
              onClick={() => handleTabClick("instructions")}
            />
            <Tab
              text="Query reference"
              active={activeTab === "query-reference"}
              onClick={() => handleTabClick("query-reference")}
            />
          </Tabs>
        )}

        <SlideoutDrawerV2.Body className={styles.drawerBody}>
          <Flex flexDirection="column" className={styles.drawerContentArea}>
            {mode === "query-only" && (
              <QueryReferenceTab
                queryId={queryId}
                arenaId={arenaId}
                dataPackageId={dataPackageId}
                dataStructureBasePath={dataStructureBasePath}
              />
            )}
            {mode === "full" && activeTab === "instructions" && (
              <InstructionsContent
                onViewQueryReference={() => handleTabClick("query-reference")}
              />
            )}
            {mode === "full" && activeTab === "query-reference" && (
              <QueryReferenceTab
                queryId={queryId}
                arenaId={arenaId}
                dataPackageId={dataPackageId}
                dataStructureBasePath={dataStructureBasePath}
              />
            )}
          </Flex>
        </SlideoutDrawerV2.Body>
      </Flex>
    </SlideoutDrawerV2>
  );
};

type InstructionsContentProps = {
  onViewQueryReference: () => void;
};

const INSTRUCTION_STEPS: readonly (
  { text: string } | { text: string; showQueryReferenceLink: true }
)[] = [
  {
    text: "Create your docx template file and then drag that file onto the template previewer. Note that only .docx format is supported, nothing else.",
  },
  { text: "Design your template in Word" },
  {
    text: "Copy and paste merge fields from your selected query onto your docx template to insert data onto your template.",
    showQueryReferenceLink: true,
  },
  {
    text: "View a preview of the template with sample data in the template previewer",
  },
  {
    text: "Adjust layout and styling as desired and then move onto upload",
  },
];

const InstructionsContent = ({ onViewQueryReference }: InstructionsContentProps) => {
  return (
    <Card isFullWidth padding="lg" className={styles.instructionsCard}>
      <Text variant="h4">Instructions on how to link and preview your template</Text>
      <Flex flexDirection="column" className={styles.instructionsSteps}>
        {INSTRUCTION_STEPS.map((step, index) => (
          <Flex
            key={step.text}
            alignItems="flex-start"
            gap={8}
            className={styles.instructionStep}
          >
            <Flex alignItems="center" justifyContent="center" className={styles.stepCounter}>
              <Text variant="strong-sm" className={styles.stepCounterLabel}>
                {index + 1}
              </Text>
            </Flex>
            <Flex flexDirection="column" gap={4} className={styles.instructionStepContent}>
              <Text variant="secondary-md">{step.text}</Text>
              {"showQueryReferenceLink" in step && step.showQueryReferenceLink && (
                <Link
                  href="#"
                  align="left"
                  icon="arrow-right"
                  iconPosition="right"
                  onClick={(event) => {
                    event.preventDefault();
                    onViewQueryReference();
                  }}
                >
                  View query reference
                </Link>
              )}
            </Flex>
          </Flex>
        ))}
      </Flex>
    </Card>
  );
};
